import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Alert } from 'react-bootstrap';
import { useAppStore } from '../../store/appStore';
import { apiService } from '../../services/api';
import { Pricing } from '../../types';
import LoadingSpinner from '../common/LoadingSpinner';

const PricingPage: React.FC = () => {
  const {
    pricing,
    loading,
    setPricing,
    setLoading,
    updatePricing,
  } = useAppStore();

  const [alert, setAlert] = useState<{ variant: 'success' | 'danger'; message: string } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState<Pricing | null>(null);
  const [editPrice, setEditPrice] = useState<number>(0);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    setLoading('pricing', true);
    try {
      const response = await apiService.getPricing();
      setPricing(response.data || []);
    } catch (error) {
      console.error('Failed to load pricing:', error);
      setAlert({ variant: 'danger', message: 'Failed to load pricing information' });
    } finally {
      setLoading('pricing', false);
    }
  };

  const handleEditClick = (pricing: Pricing) => {
    setSelectedPricing(pricing);
    setEditPrice(pricing.price);
    setShowEditModal(true);
  };

  const handleUpdatePrice = async () => {
    if (!selectedPricing || editPrice <= 0) return;

    setUpdating(true);
    try {
      await apiService.updatePrice(selectedPricing._id, editPrice);
      updatePricing(selectedPricing._id, editPrice);
      setShowEditModal(false);
      setSelectedPricing(null);
      setAlert({ variant: 'success', message: 'Price updated successfully' });
    } catch (error) {
      console.error('Failed to update price:', error);
      setAlert({ variant: 'danger', message: 'Failed to update price' });
    } finally {
      setUpdating(false);
    }
  };

  const getPriceTypeBadge = (priceType: string) => {
    return priceType === 'special' ? (
      <span className="badge bg-warning">Special</span>
    ) : (
      <span className="badge bg-primary">Normal</span>
    );
  };

  const formatDuration = (duration: number) => {
    if (duration >= 60) {
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    return `${duration}m`;
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>Pricing Management</h2>
          <p className="text-muted">Manage consultation pricing</p>
        </Col>
      </Row>

      {alert && (
        <Row className="mb-3">
          <Col>
            <Alert
              variant={alert.variant}
              onClose={() => setAlert(null)}
            >
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card className="admin-card">
            <Card.Body className="admin-card-body p-0">
              {loading.pricing ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0 modern-table">
                    <thead>
                      <tr className="table-header">
                        <th className="border-0 py-3">
                          <i className="bi bi-tag me-2 text-primary"></i>Type
                        </th>
                        <th className="border-0 py-3">
                          <i className="bi bi-hourglass-split me-2 text-primary"></i>Duration
                        </th>
                        <th className="border-0 py-3">
                          <i className="bi bi-currency-dollar me-2 text-primary"></i>Price
                        </th>
                        <th className="border-0 py-3">
                          <i className="bi bi-gear me-2 text-primary"></i>Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pricing.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center py-5">
                            <div className="text-muted">
                              <i className="bi bi-currency-dollar fs-1 mb-3 d-block"></i>
                              <h5 className="mb-2">No pricing options found</h5>
                              <p className="mb-0">No pricing configurations have been set up yet.</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        pricing.map((item, index) => (
                          <tr key={item._id} className={`table-row ${index % 2 === 0 ? 'even-row' : 'odd-row'}`}>
                            <td className="py-3">{getPriceTypeBadge(item.price_type)}</td>
                            <td className="py-3">
                              <div className="d-flex align-items-center">
                                <div className="bg-info bg-opacity-10 rounded-circle p-2 me-3">
                                  <i className="bi bi-clock text-info"></i>
                                </div>
                                <span className="fw-medium">{formatDuration(item.duration)}</span>
                              </div>
                            </td>
                            <td className="py-3">
                              <div className="d-flex align-items-center">
                                <div className="bg-success bg-opacity-10 rounded-circle p-2 me-3">
                                  <i className="bi bi-currency-dollar"></i>
                                </div>
                                <span className="fw-bold fs-5">{item.price}</span>
                              </div>
                            </td>
                            <td className="py-3">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEditClick(item)}
                                className="fw-medium"
                              >
                                <i className="bi bi-pencil me-1"></i>Edit Price
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>


      {/* Edit Price Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Price</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPricing && (
            <div>
              <div className="mb-3">
                <strong>Type:</strong> {getPriceTypeBadge(selectedPricing.price_type)}
              </div>
              <div className="mb-3">
                <strong>Duration:</strong> {formatDuration(selectedPricing.duration)}
              </div>
              <Form.Group>
                <Form.Label>New Price</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={editPrice}
                  onChange={(e) => setEditPrice(Number(e.target.value))}
                  placeholder="Enter new price"
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdatePrice}
            disabled={updating || editPrice <= 0}
          >
            {updating ? 'Updating...' : 'Update Price'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PricingPage;
